"use client";
import {
  getServiceCartItems,
  ServiceSummary,
} from "@/app/(shop)/services/[slug]/components/serviceSummary";
import Divider from "@/components/divider/divider";
import { EditAddressesModal } from "@/components/editAddressesModal/editAddressesModal";
import { IconSkeleton } from "@/components/loadingSkeleton/loadingSkeleton";
import { ScanToOrderKitchenModal } from "@/components/scanToOrder/scanToOrderCart/scanToOrderKitchenModal";
import getOrderEstimate from "@/dataLayer/getOrderEstimate";
import saveDefaultAddress from "@/dataLayer/saveDefaultAddress";
import { Address } from "@/dto/address";
import { Category } from "@/dto/category";
import { DayPart, DayPartMenuCategories } from "@/dto/dayPartMenu";
import { GiftCard } from "@/dto/giftCard";
import { Merchant } from "@/dto/merchant";
import { RetailCartItem } from "@/dto/retailCartItem";
import { ServiceCartItem } from "@/dto/serviceCartItem";
import { TableData } from "@/dto/tableData";
import { clearCart, getCartItems } from "@/utils/cartUtils";
import { cloverColorTheme } from "@/utils/cloverColorTheme";
import { giftCardCategory } from "@/utils/giftCardUtils";
import {
  getFromLocalStorage,
  saveToLocalStorage,
} from "@/utils/localStorageUtils";
import pom from "@/utils/pom";
import {
  removeFromSessionStorage,
  saveToSessionStorage,
} from "@/utils/sessionStorageUtils";
import { LocalStorageKey, SessionStorageKey } from "@/utils/storageKeys";
import { applyDataTestId } from "@/utils/testUtils";
import { getTimezoneAbbreviation } from "@/utils/timeZoneAbbreviation";
import { toastService } from "@/utils/toastService";
import {
  CloseButton,
  Popover,
  PopoverButton,
  PopoverGroup,
  PopoverPanel,
} from "@headlessui/react";
import { Bars3Icon, ChevronDownIcon } from "@heroicons/react/24/outline";
import { useTranslations } from "next-intl";
import Link from "next/link";
import { usePathname, useSearchParams } from "next/navigation";
import { useRouter } from "next13-progressbar";
import {
  Dispatch,
  SetStateAction,
  use,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useCookies } from "react-cookie";
import { Toaster } from "react-hot-toast";
import { CartDrawer } from "../cartDrawer/cartDrawer";
import {
  clearCartAction,
  ClearCartModal,
} from "../clearCartModal/clearCartModal";
import { DeliveryModalError } from "../deliveryModalError/deliveryModalError";
import { CartIcon } from "../header/cartIcon";
import { BusinessHoursModal } from "../header/hoursModal";
import { MobileCategoryModal } from "../header/mobileCategoryModal";
import { daysOfWeek } from "@/utils/dateTimeUtils";
import { RestaurantCartDrawer } from "../restaurantCartDrawer/restaurantCartDrawer";
import { ScanToOrderCart } from "../scanToOrder/scanToOrderCart/scanToOrderCart";
import { CartNumber } from "./cartNumber";
import { CategoryButton } from "./categoryButton";
import { DayPartMenuSelector } from "./dayPartMenuSelector";
import { MobileNavigationBar } from "./mobileNavigationBar";
import { NavLink } from "./navLink";
import { Perks } from "./perks";
import { SearchField } from "./searchField";
import { SearchIcon } from "./searchIcon";
import { SecondaryServicesNavigationBar } from "./secondaryServicesNavigationBar";
import { UtilityPanel } from "./utilityPanel";
import { isEmpty } from "lodash";
import { buildScanToOrderSearchParams } from "@/utils/scanToOrderUtils";
import { OrderType } from "@/types";
import { getMenuDisplayName } from "@/utils/dayPartMenuUtils";

export interface NavigationBarProps {
  merchant: Merchant;
  menu: any;
  customerPerks: any;
  merchantPreferences: any;
  deleteSession: any;
  retailCategories?: any;
  serviceCategories?: any;
  pickupTimePromise?: Promise<any>;
  defaultAddress: Address | null;
  isLoading: boolean;
  setIsCategoryMenuOpen: Dispatch<SetStateAction<boolean>>;
  isCategoryMenuOpen: boolean;
  dayPartMenus?: DayPart[];
  dayPartMenuCategories?: DayPartMenuCategories[];
  selectedMenu?: DayPart | null;
  // eslint-disable-next-line no-unused-vars
  setSelectedMenu?: (menu: DayPart | null) => void;
  isNotAcceptingOrder: boolean;
}

export const NavigationBar = ({
  merchant,
  menu,
  customerPerks,
  merchantPreferences,
  deleteSession,
  retailCategories,
  serviceCategories,
  pickupTimePromise,
  defaultAddress,
  isLoading,
  setIsCategoryMenuOpen,
  isCategoryMenuOpen,
  dayPartMenus,
  dayPartMenuCategories,
  selectedMenu = null,
  setSelectedMenu,
  isNotAcceptingOrder,
}: NavigationBarProps) => {
  const t = useTranslations("NavigationBar");
  const pathName = usePathname();
  const { push } = useRouter();
  const [cookies] = useCookies(["session", "preview", "scanToOrderInfo"]);
  const { session, scanToOrderInfo } = cookies;
  const isScanToOrderFlow = !!scanToOrderInfo;
  const currentlySavedAddress = getFromLocalStorage<Address | null>(
    LocalStorageKey.DELIVERY_ADDRESS,
    null
  );
  const [sideMenuOpen, setSideMenuOpen] = useState<boolean>(false);
  const [searchFieldOpen, setSearchFieldOpen] = useState<boolean>(false);
  const [perksModalIsOpen, setPerksModalIsOpen] = useState<boolean>(false);
  const [loginModalIsOpen, setLoginModalIsOpen] = useState<boolean>(false);
  const [storedCart, setStoredCart] = useState<
    ServiceCartItem[] | RetailCartItem[]
  >([]);
  const [isPreview, setIsPreview] = useState<boolean>(false);
  const [tempAddress, setTempAddress] = useState(
    currentlySavedAddress ? currentlySavedAddress : ""
  );
  const [isClearCartOpen, setIsClearCartOpen] = useState<boolean>(false);
  const [menuState, setMenuState] = useState<any>(menu);
  const [hoursModalIsOpen, setHoursModalIsOpen] = useState<boolean>(false);
  const [toCheckoutAfterLogin, setToCheckoutAfterLogin] =
    useState<boolean>(false);
  const [tableData, setTableData] = useState<TableData | null>();
  const [addedToCartBannerShowing, setAddedToCartBannerShowing] =
    useState<boolean>(false);
  const merchantMeta = merchant.merchantMeta;
  const merchantColor =
    merchantMeta?.colorPalette?.color || cloverColorTheme.Main.primaryColor;
  const searchParams = useSearchParams();
  const prevCartLength = useRef(storedCart.length);
  const hasMounted = useRef(false);
  const orderEstimateUpdateInterval = useRef(undefined);
  const shouldRenderRestaurantCart = merchant.isRestaurant;
  const shouldRenderRetailCart =
    merchant.isRetail ||
    merchant.isHybrid ||
    (merchant?.isService && merchant?.hasOnlyRetailItems);
  const showPerks =
    !merchant.isService ||
    (merchant.isService &&
      (merchantPreferences?.appointmentPref?.apptPayment === "FULL" ||
        merchantPreferences?.appointmentPref?.cancellationNoShowFeeEnabled));
  const customerAuthEnabled: boolean =
    process.env.NEXT_PUBLIC_AUTH_ENABLED === "true";

  const nonEssentialRestaurant =
    merchant.isRestaurant && merchant.isCheckoutEnabled;
  const nameToUseOnMenuLink = `${
    nonEssentialRestaurant ? t("menu") : t("categories")
  }`;
  const storedGiftCard = getFromLocalStorage<GiftCard | null>(
    LocalStorageKey.GIFT_CARD,
    null
  );
  let queryParams = "";
  if (scanToOrderInfo) {
    queryParams = buildScanToOrderSearchParams(scanToOrderInfo);
  }

  const isDineIn =
    searchParams?.get("order_type")?.toLowerCase() ===
    OrderType.DINE_IN.toLowerCase();

  const tempMenu = useRef<DayPart | null>(null);

  const retailPickupTime = pickupTimePromise ? use(pickupTimePromise) : null;

  useEffect(() => {
    if (
      session &&
      !currentlySavedAddress &&
      defaultAddress &&
      Object.keys(defaultAddress).length > 0
    ) {
      saveToLocalStorage(LocalStorageKey.DELIVERY_ADDRESS, defaultAddress);
      setTempAddress(defaultAddress);
    }
  }, [defaultAddress]);

  useEffect(() => {
    const timeout = setTimeout(() => {
      setAddedToCartBannerShowing(false);
    }, 3000);
    const handleStorageChange = (event: StorageEvent) => {
      if (event.key === "current-cart") {
        toastService.notifyItemAdded();
      }
    };
    window.addEventListener("storage", handleStorageChange);
    return () => {
      window.removeEventListener("storage", handleStorageChange);
      clearTimeout(timeout);
    };
  }, [addedToCartBannerShowing]);

  useEffect(() => {
    if (window.location.href?.includes("#cart")) {
      setIsCartOpen(true);
    }
    if (window.location.href?.includes("#restaurant-cart")) {
      setIsRestaurantCartOpen(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathName]);

  useEffect(() => {
    setIsPreview(window.location.hostname.startsWith("preview-"));
  }, []);

  useEffect(() => {
    const getTableData = setInterval(() => {
      const tableData = getFromLocalStorage<TableData | undefined>(
        LocalStorageKey.TABLE_DATA,
        undefined
      );
      if (tableData && scanToOrderInfo) {
        setTableData(tableData);
        if (tableData?.order?.id) {
          saveToSessionStorage(
            SessionStorageKey.RECEIPT_ID,
            tableData?.order?.id
          );
        }
      } else {
        setTableData(null);
      }
    }, 1000);
    return () => clearInterval(getTableData);
  }, []);

  useEffect(() => {
    const updatePostInfo = setInterval(() => {
      const cartItems = getCartItems();
      const serviceItems = getServiceCartItems();
      const cart: ServiceCartItem[] | RetailCartItem[] =
        serviceItems?.length > 0 ? serviceItems : cartItems;
      setStoredCart(cart);
      const currentCartLength = storedCart.reduce(
        (adder: any, item: any) => adder + item.value?.quantity,
        0
      );

      if (hasMounted.current) {
        if (currentCartLength > prevCartLength.current) {
          handleCartDrawer();
        }
      } else {
        hasMounted.current = true;
      }

      prevCartLength.current = currentCartLength;
    }, 1000);
    return () => clearInterval(updatePostInfo);
  }, []);

  let uncategorisedItems = useMemo(() => {
    return menuState?.find((category: any) => category.id === "uncategorised");
  }, [menuState]);
  let categories = useMemo(() => {
    return menuState
      ? (Object.values(
          menuState.filter((item: any) => item.id !== "uncategorised")
        ) as any[])
      : [];
  }, [menuState]);

  const uncategorized = {
    id: "99999999999999",
    items: [],
    name: t("uncategorized"),
    sortOrder: Number.MAX_SAFE_INTEGER,
  };
  if (
    uncategorisedItems &&
    uncategorisedItems?.numberOfItems != 0 &&
    !categories.find((category) => category.id === "99999999999999")
  )
    categories.push(uncategorized);

  let navigationCategories = merchantMeta?.navigationCategories;
  const mappedCategories = useMemo(() => {
    return navigationCategories
      ?.map((orderedCategory: any) => {
        const categoryDetails = categories.find(
          (category) => category.id === orderedCategory.value
        );
        return categoryDetails ? categoryDetails : null;
      })
      ?.filter((category: any) => category !== null);
  }, [categories, navigationCategories]);

  const remainingCategories = categories.filter((category) => {
    return !mappedCategories?.some(
      (mappedCategory: any) => mappedCategory.id === category.id
    );
  });

  if (merchantMeta?.selectedSetup === "Simple") {
    while (mappedCategories?.length < 5 && remainingCategories.length > 0) {
      mappedCategories.push(remainingCategories.shift());
    }
  }
  const moreCategories = remainingCategories.filter(
    (category: Category) => category.id !== giftCardCategory.id
  );

  const handlePerksModal = () => {
    setPerksModalIsOpen(!perksModalIsOpen);
  };

  const handleLoginModal = (toCheckout: boolean) => {
    setIsCartOpen(false);
    setToCheckoutAfterLogin(toCheckout);
    setLoginModalIsOpen(!loginModalIsOpen);
  };

  const handleHoursModal = () => {
    setHoursModalIsOpen(!hoursModalIsOpen);
  };
  const abbreviation = getTimezoneAbbreviation(
    merchant.timezone,
    merchant.locale
  );
  const serviceCart = getFromLocalStorage<ServiceCartItem[]>(
    LocalStorageKey.SERVICE_CART,
    []
  );
  const retailCart = getFromLocalStorage<RetailCartItem[]>(
    LocalStorageKey.CART,
    []
  );
  const [isCartOpen, setIsCartOpen] = useState<boolean>(false);
  const [isAppointmentSummaryOpen, setIsAppointmentSummaryOpen] =
    useState<boolean>(false);
  const [isRestaurantCartOpen, setIsRestaurantCartOpen] =
    useState<boolean>(false);
  const [isScanToOrderKitchenModalOpen, setIsScanToOrderKitchenModalOpen] =
    useState<boolean>(false);
  const [deliveryAddressModalOpen, setDeliveryAddressModalOpen] =
    useState<boolean>(false);
  const [deliveryAddressErrorModalOpen, setDeliveryAddressErrorModalOpen] =
    useState<boolean>(false);
  const [searchQuery, setSerchQuery] = useState("");

  const handleCartDrawer = () => {
    if (
      merchant.isService &&
      !merchant.isHybrid &&
      !merchant.hasOnlyRetailItems &&
      !storedGiftCard
    ) {
      setIsAppointmentSummaryOpen(!isAppointmentSummaryOpen);
    } else if (merchant.isRestaurant) {
      setIsRestaurantCartOpen(!isRestaurantCartOpen);
    } else if (merchant.isHybrid && retailCart.length >= 1) {
      setIsCartOpen(!isCartOpen);
    } else if (
      merchant.isHybrid &&
      (serviceCart?.length >= 1 || (!retailCart.length && !storedGiftCard))
    )
      setIsAppointmentSummaryOpen(!isAppointmentSummaryOpen);
    else {
      setIsCartOpen(!isCartOpen);
    }
  };

  const onDayPartMenuChange = useCallback(
    (menu: DayPart | null) => {
      tempMenu.current = menu;
      if (retailCart.length > 0 && !isDineIn && selectedMenu?.id !== menu?.id) {
        setIsClearCartOpen(true);
        return;
      }

      switchDayPartMenu();
    },
    [retailCart, isDineIn]
  );

  const onConfirmation = () => {
    clearCart();
    setIsClearCartOpen(false);
    switchDayPartMenu();
  };

  const switchDayPartMenu = () => {
    if (tempMenu.current) {
      setSelectedMenu?.(tempMenu.current);
      removeFromSessionStorage(SessionStorageKey.SCHEDULED_TIME);
      removeFromSessionStorage(SessionStorageKey.PICKUP_TIME);
      saveToSessionStorage(SessionStorageKey.DAYPART_MENU, {
        id: tempMenu.current.id,
        menuId: tempMenu.current.menuId,
        name: tempMenu.current.formattedName,
      });
      const params = new URLSearchParams(searchParams || "");
      params.set("menu", tempMenu.current?.formattedName);
      push(`/menu/all?${params.toString()}`);
    }
  };

  const clearCartActions: clearCartAction[] = [
    {
      text: t("cancel"),
      onClick: () => setIsClearCartOpen(false),
      type: "secondary",
    },
    {
      text: t("clearCartAndSwitchMenu"),
      onClick: onConfirmation,
      type: "primary",
    },
  ];

  useEffect(() => {
    if (!selectedMenu) return;
    const categories =
      dayPartMenuCategories?.find((o) => o.menuId === selectedMenu?.menuId)
        ?.categories || [];
    setMenuState(categories);
  }, [selectedMenu]);

  // update order estimate every 60 seconds if once of the carts is open
  useEffect(() => {
    const updateOrderEstimates = async () => {
      const orderEstimates = await getOrderEstimate(
        merchant.id,
        deliveryAddress,
        merchant?.isRetail ? retailPickupTime?.pickupDateTimeMillis : undefined
      );
      saveToSessionStorage(SessionStorageKey.ORDER_ESTIMATES, orderEstimates);
    };
    const deliveryAddress = getFromLocalStorage<Partial<Address>>(
      LocalStorageKey.DELIVERY_ADDRESS,
      {}
    );
    if (
      (isCartOpen || isRestaurantCartOpen) &&
      !isEmpty(deliveryAddress) &&
      !orderEstimateUpdateInterval.current
    ) {
      updateOrderEstimates();
      (orderEstimateUpdateInterval.current as any) = setInterval(async () => {
        updateOrderEstimates();
      }, 60 * 1000);
    } else {
      clearInterval(orderEstimateUpdateInterval.current);
      orderEstimateUpdateInterval.current = undefined;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isCartOpen, isRestaurantCartOpen]);

  const handleSearch = (event: { key: string }) => {
    if (event.key === "Enter") {
      const params = new URLSearchParams(searchParams || "");
      if (searchQuery) {
        params.set("query", searchQuery);
      } else {
        params.delete("query");
      }
      if (pathName?.toLocaleLowerCase().includes("shop")) {
        push(`/shop?${params.toString()}`);
      } else if (merchant.isService) {
        push(`/services/all?${params.toString()}`);
      } else if (merchant.isRestaurant) {
        push(`/menu/all?${params.toString()}`);
      } else if (merchant.isEssentialMerchant) {
        push(`/categories/all?${params.toString()}`);
      } else {
        push(`/all?${params.toString()}`);
      }
      setSearchFieldOpen(false);
    }
  };

  const showMoreDropDown = () => {
    // if we already display the only item in the more categories
    const currentCategoryInPathName = decodeURIComponent(pathName!).includes(
      moreCategories[0]?.name
    );
    if (
      (moreCategories.length === 1 && currentCategoryInPathName) ||
      moreCategories.length < 1
    ) {
      return false;
    } else {
      return true;
    }
  };

  const moreDropDown = showMoreDropDown();

  return (
    <>
      {isClearCartOpen && (
        <ClearCartModal
          isOpen={isClearCartOpen}
          onRequestClose={() => setIsClearCartOpen(false)}
          merchantColor={merchantColor}
          addToCart={onConfirmation}
          actions={clearCartActions}
          title={t("clearCartTitle", {
            menuName: getMenuDisplayName(selectedMenu?.menuName, t),
          })}
          message={t("clearCartMessage", {
            menuName: getMenuDisplayName(tempMenu.current?.menuName, t),
          })}
        />
      )}
      <div className="flex w-full items-center justify-between sticky sm:relative top-0 z-40">
        <Toaster />
        <div className="flex sm:hidden py-6 px-3 sm:px-8 ">
          <button
            type="button"
            onClick={() => setSideMenuOpen(true)}
            className="-m-2.5 inline-flex items-center justify-center rounded-md p-2.5 text-gray-700"
          >
            <span className="sr-only">{t("openMainMenu")}</span>
            <Bars3Icon aria-hidden="true" className="h-6 w-6" />
          </button>
        </div>
        {searchFieldOpen && (
          <SearchField
            {...{
              searchParams,
              setSerchQuery,
              handleSearch,
              setSearchFieldOpen,
            }}
          />
        )}
        {!searchFieldOpen && (
          <>
            {!merchant.isRetail &&
              (pathName?.includes("services") ||
                pathName?.includes("shop") ||
                pathName?.includes("categories") ||
                pathName?.includes("menu")) && (
                <CategoryButton
                  setIsCategoryMenuOpen={setIsCategoryMenuOpen}
                  merchant={merchant}
                  selectedDayPart={selectedMenu}
                />
              )}
            <div
              className={`flex flex-col sm:w-full items-end sm:items-center justify-between ${
                merchant.isRetail ||
                (!pathName?.includes("shop") &&
                  !pathName?.includes("services") &&
                  !pathName?.includes("categories") &&
                  !pathName?.includes("menu"))
                  ? "w-full"
                  : ""
              }`}
            >
              <nav
                aria-label="Global"
                data-testid={applyDataTestId(pom.header.navigationBar)}
                className={[
                  "flex",
                  "items-center",
                  "justify-between",
                  "sm:w-full",
                  "sm:px-10",
                  "sm:h-[60px]",
                ].join(" ")}
              >
                <PopoverGroup className="hidden h-full sm:flex pt-5">
                  {merchantMeta?.selectedSetup !== "Simple" && (
                    <NavLink
                      name={t("about")}
                      nameToUse="about"
                      style={{ borderColor: merchantColor }}
                      showBottomBorder={pathName?.includes("/about")}
                      queryParams={queryParams}
                    />
                  )}
                  {merchant.isService && !merchant.hasOnlyRetailItems && (
                    <NavLink
                      name={t("services")}
                      nameToUse="services/all"
                      style={{ borderColor: merchantColor }}
                      showBottomBorder={pathName?.includes("/services")}
                    />
                  )}
                  {(merchant.isHybrid ||
                    (merchant.isService && merchant.hasOnlyRetailItems)) && (
                    <NavLink
                      name={t("shop")}
                      nameToUse="shop"
                      style={{ borderColor: merchantColor }}
                      showBottomBorder={pathName?.includes("/shop")}
                    />
                  )}
                  {(merchant.isRestaurant || merchant.isEssentialMerchant) && (
                    <>
                      {dayPartMenus && dayPartMenus.length > 0 ? (
                        <DayPartMenuSelector
                          dayPartMenus={dayPartMenus}
                          selectedMenu={selectedMenu}
                          onMenuChange={onDayPartMenuChange}
                          merchant={merchant}
                          style={{ borderColor: merchantColor }}
                          showBottomBorder={
                            t("menuCategories").includes(
                              nameToUseOnMenuLink?.toLowerCase()
                            ) &&
                            pathName?.includes(
                              nameToUseOnMenuLink?.toLowerCase()
                            )
                          }
                        />
                      ) : (
                        <NavLink
                          name={`${nameToUseOnMenuLink}`}
                          nameToUse={`${nameToUseOnMenuLink.toLowerCase()}/all`}
                          style={{ borderColor: merchantColor }}
                          showBottomBorder={
                            t("menuCategories").includes(
                              nameToUseOnMenuLink?.toLowerCase()
                            ) &&
                            pathName?.includes(
                              nameToUseOnMenuLink?.toLowerCase()
                            )
                          }
                        />
                      )}
                    </>
                  )}
                  {merchant.isRetail &&
                    mappedCategories?.map((c: { id: string; name: string }) => (
                      <NavLink
                        key={c.id}
                        name={encodeURIComponent(c.name)}
                        style={{ borderColor: merchantColor }}
                        showBottomBorder={false}
                      />
                    ))}
                  {moreCategories.some(
                    (c: { id: string; name: string }) =>
                      decodeURIComponent(pathName || "").replace("/", "") ===
                      c.name
                  ) && (
                    <NavLink
                      name={(pathName || "").substring(1)}
                      style={{ borderColor: merchantColor }}
                      showBottomBorder={
                        ((pathName || "").substring(1) === "services" &&
                          pathName?.includes("/services")) ||
                        (t("menuCategories").includes(
                          (pathName || "").substring(1)
                        ) &&
                          pathName?.includes(nameToUseOnMenuLink))
                      }
                    />
                  )}

                  {merchant.isRetail && moreDropDown && (
                    <Popover className="relative">
                      <PopoverButton className="w-full h-full capitalize leading-6 text-gray-900 px-4 hover:bg-gray-50 text-center pb-3 text-[16px] flex gap-x-2 items-center link">
                        {t("more")}
                        <ChevronDownIcon
                          aria-hidden="true"
                          className="h-5 w-5 flex-none text-gray-900"
                        />
                      </PopoverButton>
                      <PopoverPanel
                        transition
                        className="absolute -left-8 top-full z-20 mt-3 w-screen max-w-[400px] overflow-hidden rounded-3xl bg-white shadow-lg ring-1 ring-gray-900/5 transition data-[closed]:translate-y-1 data-[closed]:opacity-0 data-[enter]:duration-200 data-[leave]:duration-150 data-[enter]:ease-out data-[leave]:ease-in max-h-[500px] overflow-y-auto p-3"
                      >
                        {moreCategories
                          .filter(
                            (c: { id: string; name: string }) =>
                              decodeURIComponent(pathName || "").replace(
                                "/",
                                ""
                              ) !== c.name
                          )
                          .map((c: { id: string; name: string }) => (
                            <div
                              key={c.id}
                              className="group relative flex items-center gap-x-6 rounded-lg p-3 text-sm leading-6 hover:bg-gray-50"
                            >
                              <div className="flex-auto">
                                <Link
                                  href={`/${c.name}`}
                                  as={`/${encodeURIComponent(c.name)}`}
                                  className={[
                                    "block text-gray-900",
                                    "font-normal text-base leading-6",
                                  ].join(" ")}
                                >
                                  <CloseButton>
                                    {" "}
                                    {c.name}
                                    <span className="absolute inset-0" />
                                  </CloseButton>
                                </Link>
                              </div>
                            </div>
                          ))}
                      </PopoverPanel>
                    </Popover>
                  )}
                  {merchant.isSellingGiftCards &&
                    !isScanToOrderFlow &&
                    !isLoading && (
                      <NavLink
                        name={t("giftCard")}
                        nameToUse="giftcard"
                        style={{ borderColor: merchantColor }}
                        showBottomBorder={pathName?.includes("/giftcard")}
                        data-testid="gift-card-navlink"
                      />
                    )}
                </PopoverGroup>
                <div
                  className={[
                    "flex",
                    "flex-row",
                    "gap-5",
                    "items-center",
                    "mr-4",
                  ].join(" ")}
                >
                  {session &&
                    customerPerks?.length > 0 &&
                    showPerks &&
                    merchant.isCheckoutEnabled &&
                    !tableData && (
                      <Perks
                        {...{
                          handlePerksModal,
                          perksModalIsOpen,
                          customerPerks,
                        }}
                      />
                    )}
                  {!isPreview && !pathName?.includes("/giftcard") && (
                    <div
                      className={["cursor-pointer"].join(" ")}
                      onClick={() => setSearchFieldOpen(true)}
                    >
                      <SearchIcon />
                    </div>
                  )}
                  <BusinessHoursModal
                    isOpen={hoursModalIsOpen}
                    onRequestClose={handleHoursModal}
                    daysOfWeek={daysOfWeek}
                    businessHours={merchant?.businessHours?.hours}
                    timezone={abbreviation}
                  />
                  {!isPreview &&
                    merchant.isCheckoutEnabled &&
                    customerAuthEnabled &&
                    !isScanToOrderFlow &&
                    (isLoading ? (
                      <IconSkeleton width={20} height={20} />
                    ) : (
                      <UtilityPanel
                        {...{
                          session,
                          handleLoginModal,
                          loginModalIsOpen,
                          setLoginModalIsOpen,
                          deleteSession,
                          merchantColor,
                          toCheckoutAfterLogin,
                        }}
                        merchant={merchant}
                      />
                    ))}
                  {merchant.isCheckoutEnabled && !isPreview && (
                    <div
                      className={[
                        "cursor-pointer",
                        "relative",
                        "inline-flex",
                        "sm:mr-0",
                      ].join(" ")}
                      onClick={handleCartDrawer}
                      data-testid={applyDataTestId(pom.landingPage.cartIcon)}
                    >
                      <CartIcon />
                      <CartNumber
                        merchantColor={merchantColor}
                        storedCart={storedCart}
                        storedGiftCard={storedGiftCard}
                        tableData={tableData}
                      />
                    </div>
                  )}
                  {shouldRenderRetailCart && (
                    <CartDrawer
                      isOpen={isCartOpen}
                      onClose={() => setIsCartOpen(false)}
                      onRequestClose={handleCartDrawer}
                      merchant={merchant}
                      onTriggerLoginModal={handleLoginModal}
                      retailPickupTime={retailPickupTime}
                      setDeliveryAddressModalOpen={setDeliveryAddressModalOpen}
                      setIsCartOpen={setIsCartOpen}
                      setDeliveryAddressErrorModalOpen={
                        setDeliveryAddressErrorModalOpen
                      }
                    />
                  )}
                  <ServiceSummary
                    isOpen={isAppointmentSummaryOpen}
                    onClose={() => setIsAppointmentSummaryOpen(false)}
                    onRequestClose={() => setIsAppointmentSummaryOpen(false)}
                    merchant={merchant}
                    onTriggerLoginModal={handleLoginModal}
                    merchantPreferences={merchantPreferences}
                    merchantColor={merchantColor}
                    setIsCartOpen={setIsAppointmentSummaryOpen}
                  />
                  {!tableData && shouldRenderRestaurantCart && (
                    <RestaurantCartDrawer
                      isOpen={isRestaurantCartOpen}
                      onClose={() => setIsRestaurantCartOpen(false)}
                      onRequestClose={handleCartDrawer}
                      merchant={merchant}
                      onTriggerLoginModal={handleLoginModal}
                      setDeliveryAddressModalOpen={setDeliveryAddressModalOpen}
                      setIsRestaurantCartOpen={setIsRestaurantCartOpen}
                      setDeliveryAddressErrorModalOpen={
                        setDeliveryAddressErrorModalOpen
                      }
                      selectedDayPart={selectedMenu}
                    />
                  )}
                  {tableData && (
                    <ScanToOrderCart
                      isOpen={isRestaurantCartOpen}
                      onRequestClose={handleCartDrawer}
                      merchant={merchant}
                      tableData={tableData}
                      setIsSentToKitchen={setIsScanToOrderKitchenModalOpen}
                      scanToOrderInfo={scanToOrderInfo}
                      isRunningTabsEnabled={merchant.isRunningTabsEnabled}
                      selectedDayPart={selectedMenu}
                    />
                  )}
                  <ScanToOrderKitchenModal
                    isOpen={isScanToOrderKitchenModalOpen}
                    onRequestClose={() =>
                      setIsScanToOrderKitchenModalOpen(false)
                    }
                    merchant={merchant}
                    scanToOrderInfo={scanToOrderInfo}
                  />
                  <EditAddressesModal
                    setDefaultDeliveryAllowed={!!session}
                    merchant={merchant}
                    merchantColor={merchantColor}
                    isOpen={deliveryAddressModalOpen}
                    onRequestClose={() => setDeliveryAddressModalOpen(false)}
                    onUpdated={async (
                      address: any,
                      isDefaultDelivery: boolean
                    ) => {
                      setTempAddress(address);
                      try {
                        const result = await getOrderEstimate(
                          merchant.id,
                          address
                        );
                        if (isDefaultDelivery) {
                          await saveDefaultAddress(address);
                        }
                        saveToSessionStorage(
                          SessionStorageKey.ORDER_ESTIMATES,
                          result
                        );
                        saveToLocalStorage(
                          LocalStorageKey.DELIVERY_ADDRESS,
                          address
                        );
                        if (merchant.isRetail) {
                          window.dispatchEvent(
                            new StorageEvent("storage", { key: "cart" })
                          );
                        } else if (merchant.isRestaurant) {
                          window.dispatchEvent(
                            new StorageEvent("storage", {
                              key: "restaurant-cart",
                            })
                          );
                        }
                      } catch {
                        setDeliveryAddressModalOpen(false);
                        setDeliveryAddressErrorModalOpen(true);
                      }
                    }}
                    checkoutCloseHandler={() => {}}
                  />
                  <DeliveryModalError
                    onRequestClose={() =>
                      setDeliveryAddressErrorModalOpen(false)
                    }
                    merchantColor={merchantColor}
                    isOpen={deliveryAddressErrorModalOpen}
                    merchant={merchant}
                    address={tempAddress}
                    onEnterNewAddress={() => {
                      setDeliveryAddressErrorModalOpen(false);
                      setDeliveryAddressModalOpen(true);
                    }}
                    checkoutCloseHandler={() => {}}
                  />
                </div>
              </nav>
              <Divider classNames="w-[calc(100%-80px)]"></Divider>
              {(pathName?.includes("/services") ||
                pathName?.includes("/menu") ||
                (pathName?.includes("/shop") && merchant.isHybrid)) && (
                <SecondaryServicesNavigationBar
                  displayCategories={mappedCategories}
                  moreCategories={moreCategories}
                  retailCategories={retailCategories}
                  serviceCategories={serviceCategories}
                  scanToOrderInfo={scanToOrderInfo}
                  isNotAcceptingOrder={isNotAcceptingOrder}
                  isSimpleStorefront={merchantMeta?.selectedSetup === "Simple"}
                />
              )}
            </div>
          </>
        )}
        <MobileNavigationBar
          categories={categories}
          sideMenuOpen={sideMenuOpen}
          setSideMenuOpen={setSideMenuOpen}
          scanToOrderInfo={scanToOrderInfo}
          merchant={merchant}
          dayPartMenus={dayPartMenus}
          onDayPartMenuChange={onDayPartMenuChange}
          selectedMenu={selectedMenu}
        />
        {!merchant.isRetail &&
          (pathName?.includes("all") || pathName?.includes("shop")) && (
            <MobileCategoryModal
              menu={menuState}
              setIsCategoryMenuOpen={setIsCategoryMenuOpen}
              isCategoryMenuOpen={isCategoryMenuOpen}
              merchant={merchant}
              serviceCategories={serviceCategories}
              retailCategories={retailCategories}
            />
          )}
      </div>
    </>
  );
};
